import { Injectable } from '@angular/core';
import { SessionStorageService } from './session-storage.service';
import { fromEvent, Observable, Subject, Subscription, interval } from 'rxjs';
import { PageSession } from '../models/page-session.model';
import { environment } from '../../../environments/environment';
import { MaintenanceService } from './maintenance.service';
import { Router } from '@angular/router';

const SESSION_INTERVAL_MILLIS: number = 3 * 1000; // 3 seconds
const SLEEP_TIMEOUT_MILLIS: number = 4 * 60 * 1000; // 4 minutes

const SESSION_TIMEOUT_MILLIS: number = 6 * 60 * 60 * 1000; // 6 hours

const TEACHER_REVIEW_SESSION_TIMEOUT_MILLIS: number = 60 * 60 * (environment.teacherReviewTimeoutHour || 1) * 1000; // 1 hour

const MAINTENANCE_CHECK_INTERVAL_MILLIS: number = 1 * 30 * 1000; // 30 seconds

export enum LogoutReason {
  INACTIVITY = 'logout_inactivity',
  SLEEP_TIMEOUT = 'logout_sleep_timeout',
  MAINTENANCE_SCHEDULED = 'logout_maintenance_scheduled',
  MANUAL = 'logout_manual',
  ERROR = 'logout_error'
}

@Injectable({
  providedIn: 'root'
})
export class SessionTimerService {
  private inactivityTimer: any = null;
  private sleepInterval: any = null;
  private maintenanceCheckIntervalSubscription: Subscription | null = null;
  private lastTime: number = Date.now();
  private logoutTrigger$: Subject<LogoutReason> = new Subject<LogoutReason>();
  private clickInactivityResetListener$: Observable<Event>;
  private touchInactivityResetListener$: Observable<Event>;
  private clickResetSubscription: Subscription | null = null;
  private touchResetSubscription: Subscription | null = null;
  private pageSessions: PageSession[] = [];

  private scheduledMaintenanceTime: Date | null = null;

  /**
   * We have two timing events in the timing service that can cause a logout:
   * (1) one that will look for inactivity and if the user has not been active in the last 6 hours, logout
   * (2) one that checks every 3 seconds to see if we haven't done a check for 4 minutes (indicating we fell asleep) , logout
   **/
  constructor(
    private sessionStorageService: SessionStorageService,
    private maintenanceService: MaintenanceService,
    private router: Router
  ) {
    this.clickInactivityResetListener$ = fromEvent(document, 'click');
    this.touchInactivityResetListener$ = fromEvent(document, 'touchstart');

    this.logoutTrigger$.subscribe(reason => {
      this.handleLogout(reason);
    });
  }

  public getScheduledMaintenanceTime(): Date | null {
    return this.scheduledMaintenanceTime;
  }

  startSleepAndInactivityTimers() {
    this.cancelTimers();

    const sessionTimeout = this.getTimeoutThreshold();
    this.sessionStorageService.setSessionExpirationTime((new Date().getTime()) + sessionTimeout);

    this.startTimers();
    this.startMaintenanceChecks();
  }

  private startTimers() {
    let currentTime: number = (new Date()).getTime();
    let sessionExpiration: number | null = this.sessionStorageService.getSessionExpirationTime();

    if (sessionExpiration !== null && currentTime > sessionExpiration) {
      this.triggerLogout(LogoutReason.SLEEP_TIMEOUT);
      return;
    }

    this.startInactivityTimer();
    this.startSleepTimer();
    this.subscribeToActivityResets();
  }

  public getTimeoutThreshold(): number {
    const isTeacherReview = this.sessionStorageService.userData()?.isTeacherReviewUser() ?? false;
    return isTeacherReview ? TEACHER_REVIEW_SESSION_TIMEOUT_MILLIS : SESSION_TIMEOUT_MILLIS;
  }

  private startSleepTimer() {
    this.cancelSleepTimer();
    this.lastTime = Date.now();

    this.sleepInterval = setInterval(() => {
      let currentTime = (new Date()).getTime();

      if (this.scheduledMaintenanceTime && currentTime >= this.scheduledMaintenanceTime.getTime() * 1000) {
        this.triggerLogout(LogoutReason.MAINTENANCE_SCHEDULED);
        return;
      }

      if (currentTime > (this.lastTime + SLEEP_TIMEOUT_MILLIS)) {
        this.triggerLogout(LogoutReason.SLEEP_TIMEOUT);
        return;
      }

      this.lastTime = currentTime;
    }, SESSION_INTERVAL_MILLIS);
  }

  private cancelSleepTimer() {
    if (this.sleepInterval != null) {
      clearInterval(this.sleepInterval);
      this.sleepInterval = null;
    }
  }

  private startInactivityTimer() {
    this.resetInactivityTimer();
  }

  private subscribeToActivityResets() {
    this.unsubscribeFromActivityResets();

    const activityHandler = () => {
      if (this.sessionStorageService.isUserLoggedIn()) {
        this.resetInactivityTimer();
      } else {
        this.cancelTimers();
      }
    };

    this.clickResetSubscription = this.clickInactivityResetListener$.subscribe(activityHandler);
    this.touchResetSubscription = this.touchInactivityResetListener$.subscribe(activityHandler);
  }

  private unsubscribeFromActivityResets() {
    this.clickResetSubscription?.unsubscribe();
    this.touchResetSubscription?.unsubscribe();
    this.clickResetSubscription = null;
    this.touchResetSubscription = null;
  }

  private resetInactivityTimer() {
    this.cancelInactivityTimer();
    const sessionTimeout = this.getTimeoutThreshold();

    this.inactivityTimer = setTimeout(() => {
      this.triggerLogout(LogoutReason.INACTIVITY);
    }, sessionTimeout);
  }

  private cancelInactivityTimer() {
    if (this.inactivityTimer != null) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  private startMaintenanceChecks() {
    this.cancelMaintenanceChecks();
    this.checkForMaintenanceAndUpdateState();

    this.maintenanceCheckIntervalSubscription = interval(MAINTENANCE_CHECK_INTERVAL_MILLIS).subscribe(() => {
      if (this.sessionStorageService.isUserLoggedIn()) {
        this.checkForMaintenanceAndUpdateState();
      } else {
        this.cancelMaintenanceChecks();
      }
    });
  }

  private cancelMaintenanceChecks() {
    this.maintenanceCheckIntervalSubscription?.unsubscribe();
    this.maintenanceCheckIntervalSubscription = null;
  }

  public async checkForMaintenanceAndUpdateState() {
    try {
      const timeString = await this.maintenanceService.checkForScheduledMaintenanceChanges() || null;
      this.setScheduledMaintenanceTime(timeString);
    } catch (error) {
      console.error('Failed to check for scheduled maintenance changes:', error);
    }
  }

  private setScheduledMaintenanceTime(timeString: string | null) {
    if (timeString) {

      const timeAsNumber = Number(timeString);
      const newTime = new Date(timeAsNumber * 1000);
      if (!isNaN(newTime.getTime()) && (!this.scheduledMaintenanceTime || newTime.getTime() !== this.scheduledMaintenanceTime.getTime())) {
        this.scheduledMaintenanceTime = newTime;

      } else if (isNaN(newTime.getTime())) {
        console.warn('Received invalid maintenance time string:', timeString);
      }
    } else {
      if (this.scheduledMaintenanceTime !== null) {
        this.scheduledMaintenanceTime = null;
      }
    }
  }

  public triggerLogout(reason: LogoutReason) {
    if (this.sleepInterval || this.inactivityTimer || this.maintenanceCheckIntervalSubscription) {
      console.log(`Logout triggered. Reason: ${reason}`);
      this.logoutTrigger$.next(reason);
    } else {
      console.log(`Logout trigger skipped, already logged out or timers stopped. Reason: ${reason}`);
    }
  }

  private handleLogout(reason: LogoutReason) {
    this.cancelTimers();

    let message = 'Your session has ended. Please log in again.';
    switch (reason) {
      case LogoutReason.INACTIVITY:
        message = 'You have been logged out due to inactivity.';
        break;
      case LogoutReason.SLEEP_TIMEOUT:
        message = 'Your session timed out while the application was inactive.';
        break;
      case LogoutReason.MAINTENANCE_SCHEDULED:
        message = 'You have been logged out because scheduled maintenance is starting.';
        break;
      case LogoutReason.MANUAL:
        message = '';
        break;
      case LogoutReason.ERROR:
        message = 'An error occurred, forcing logout. Please log in again.';
        break;
    }
    if (message) {
      this.sessionStorageService.setLogoutMessage(message);
    }

    this.sessionStorageService.clearUserData();
    this.sessionStorageService.clearSessionExpirationTime();

    this.scheduledMaintenanceTime = null;
    this.clearPageSessions();

    this.router.navigate(['/login']);
  }

  getLogoutTrigger(): Subject<LogoutReason> {
    return this.logoutTrigger$;
  }

  manualLogout() {
    this.triggerLogout(LogoutReason.MANUAL);
  }

  cancelTimers() {
    this.cancelInactivityTimer();
    this.cancelSleepTimer();
    this.cancelMaintenanceChecks();
    this.unsubscribeFromActivityResets();
    this.scheduledMaintenanceTime = null;
  }

  startPageSession(currentPage: string, previousPage: string | null = null) {
    const newSession: PageSession = {
      startTime: Date.now(),
      currentPage,
      previousPage
    };
    this.pageSessions.push(newSession);
  }

  endPageSession(currentPage: string) {
    const session = this.pageSessions.find(s => s.currentPage === currentPage && !s.endTime);
    if (session) {
      session.endTime = Date.now();
    }
  }

  getPageSessions(): PageSession[] {
    return this.pageSessions;
  }

  clearPageSessions() {
    this.pageSessions = [];
  }
}
